import { useState, useCallback } from 'react';
import { Icon } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { TriggerType } from './TriggerType';
import {
  type TInputMappings,
  type TConfigurationValues,
  type ITriggerFieldGroup,
} from '../WebhookTriggerDialog';
import type { IPollTriggerProps } from './types';
import { PollTriggerDialog } from '../PollTriggerDialog/PollTriggerDialog';
import { useCreateTrigger } from '../PollTriggerDialog/use-create-trigger';
import type { IPollTriggerSaveData } from '../PollTriggerDialog/types';
import { useResumeTriggerConnection } from '@/modules/triggers/hooks/use-resume-trigger-connection';
import { createPollTriggerConfiguration } from './utils';

/**
 * PollTrigger Component
 *
 * Handles the poll trigger type selection and configuration.
 * Encapsulates the poll dialog and trigger creation logic.
 *
 * Flow:
 * 1. User clicks the poll trigger type button
 * 2. Opens PollTriggerDialog for configuration
 * 3. On save, creates trigger instance in BDK
 * 4. Sets automations for the trigger instance in BDK
 * 5. Then creates automation trigger in Grimoire with input mappings
 *
 * @param props - Component props
 * @returns TriggerType component for webhook with integrated dialog
 */
export function PollTrigger({
  inputSpecs,
  automationId,
  isDraft,
  presentation = 'card',
  onOpen,
  open,
  onOpenChange,
  hideTrigger = false,
}: IPollTriggerProps) {
  const { t } = useTranslation('automation-inputs');

  // Poll dialog open state
  const {
    value: isPollDialogOpen,
    setTrue: openPollDialog,
    setValue: setIsPollDialogOpen,
  } = useBoolean(false);

  // Controlled state for poll dialog
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedIntegration, setSelectedIntegration] = useState<
    string | undefined
  >();
  const [selectedIntegrationVersion, setSelectedIntegrationVersion] = useState<
    string | undefined
  >();
  const [selectedConnection, setSelectedConnection] = useState<
    string | undefined
  >();
  const [selectedPollId, setSelectedPollId] = useState<string | undefined>();
  const [selectedPollIntervalMinutes, setSelectedPollIntervalMinutes] =
    useState<number | undefined>();

  const [inputMappings, setInputMappings] = useState<TInputMappings>({});
  const [triggerFieldGroups, setTriggerFieldGroups] = useState<
    ITriggerFieldGroup[]
  >([]);

  const [configurationValues, setConfigurationValues] =
    useState<TConfigurationValues>({});

  // Use the generic createTrigger hook
  const { createTrigger, isLoading } = useCreateTrigger({
    automationId,
    isDraft,
    onSuccess: () => handleDialogOpenChange(false),
    toastMessages: {
      success: t('pollDialog.toast.success'),
      error: t('pollDialog.toast.error'),
      errorNoTriggerId: t('pollDialog.toast.errorNoTriggerId'),
    },
    sentryTags: {
      component: 'PollTrigger',
      action: 'handlePollTriggerSave',
    },
  });

  /**
   * Handles poll trigger save.
   * Creates trigger instance in BDK and automation trigger in Grimoire.
   *
   * @param data - Poll trigger save data containing integration, connection, and mapping details
   */
  const handlePollTriggerSave = useCallback(
    async (data: IPollTriggerSaveData) => {
      const {
        selectedIntegration,
        selectedIntegrationVersion,
        selectedConnection,
        selectedPollId,
        selectedPollIntervalMinutes,
        inputMappings,
        configurationValues,
      } = data;

      // Validate required fields
      if (
        !selectedIntegration ||
        !selectedIntegrationVersion ||
        !selectedConnection ||
        !selectedPollId ||
        !selectedPollIntervalMinutes
      ) {
        return;
      }

      // Convert configuration values to BDK format
      const configuration = createPollTriggerConfiguration(configurationValues);

      // Call the generic createTrigger
      await createTrigger({
        bookName: selectedIntegration,
        bookVersion: selectedIntegrationVersion,
        connectionId: selectedConnection,
        pollId: selectedPollId,
        pollIntervalMinutes: selectedPollIntervalMinutes,
        configuration,
        inputMappings,
      });
    },
    [createTrigger],
  );

  /**
   * Handles integration selection change, clearing dependent fields
   * (connection, poll, interval) so stale values from the previous
   * integration can't keep the Save button enabled with an invalid
   * combination.
   */
  const handleSelectedIntegrationChange = useCallback(
    (integration: string | undefined) => {
      setSelectedIntegration(integration);
      setSelectedConnection(undefined);
      setSelectedPollId(undefined);
      setSelectedPollIntervalMinutes(undefined);
      setTriggerFieldGroups([]);
    },
    [],
  );

  /**
   * Resets dialog state to initial values for create mode
   */
  const resetDialogState = useCallback(() => {
    setCurrentStep(1);
    setSelectedIntegration(undefined);
    setSelectedIntegrationVersion(undefined);
    setSelectedConnection(undefined);
    setSelectedPollId(undefined);
    setSelectedPollIntervalMinutes(undefined);
    setInputMappings({});
    setTriggerFieldGroups([]);
    setConfigurationValues({});
  }, []);

  useResumeTriggerConnection({
    kind: 'poll',
    onResume: useCallback(
      ({ integrationName, integrationVersion, connectionId }) => {
        setCurrentStep(1);
        setSelectedIntegration(integrationName);
        setSelectedIntegrationVersion(integrationVersion);
        setSelectedConnection(connectionId);
        setIsPollDialogOpen(true);
        onOpenChange?.(true);
      },
      [setIsPollDialogOpen, onOpenChange],
    ),
  });

  const handleDialogOpenChange = (open: boolean) => {
    setIsPollDialogOpen(open);
    onOpenChange?.(open);
    if (!open) {
      resetDialogState();
    }
  };

  const handleOpen = () => {
    onOpen?.();
    openPollDialog();
    onOpenChange?.(true);
  };

  return (
    <>
      {!hideTrigger && (
        <TriggerType
          icon={
            <Icon
              type="RefreshCw"
              size="lg"
              strokeWidth={1.5}
              className="text-ring"
            />
          }
          title={t('triggerTypes.poll.title')}
          description={t('triggerTypes.poll.description')}
          onClick={handleOpen}
          presentation={presentation}
        />
      )}

      {/* Poll Trigger Dialog */}
      <PollTriggerDialog
        isOpen={open ?? isPollDialogOpen}
        onOpenChange={handleDialogOpenChange}
        inputSpecs={inputSpecs}
        onSave={handlePollTriggerSave}
        isLoading={isLoading}
        // Controlled state props
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        selectedIntegration={selectedIntegration}
        onSelectedIntegrationChange={handleSelectedIntegrationChange}
        selectedIntegrationVersion={selectedIntegrationVersion}
        onSelectedIntegrationVersionChange={setSelectedIntegrationVersion}
        selectedConnection={selectedConnection}
        onSelectedConnectionChange={setSelectedConnection}
        selectedPollId={selectedPollId}
        onSelectedPollChange={setSelectedPollId}
        inputMappings={inputMappings}
        onInputMappingsChange={setInputMappings}
        selectedPollIntervalMinutes={selectedPollIntervalMinutes}
        onSelectedPollIntervalMinutesChange={setSelectedPollIntervalMinutes}
        configurationValues={configurationValues}
        onConfigurationValuesChange={setConfigurationValues}
        triggerFieldGroups={triggerFieldGroups}
        onTriggerFieldGroupsChange={setTriggerFieldGroups}
      />
    </>
  );
}
